import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { useLayout } from "@/context/LayoutContext";
import { Ticket, Clock, CheckCircle, XCircle } from "lucide-react";
import QRCodeModal from "@/components/QRCodeModal";
import { Button } from "@/components/ui/button";
import { formatDistanceToNow } from "date-fns";
import { de } from "date-fns/locale";

export default function MyCouponsPage() {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCoupon, setSelectedCoupon] = useState(null);
  const { user, token, setShowAuthModal } = useAuth();
  const { theme } = useLayout();

  useEffect(() => {
    if (user) {
      fetchMyCoupons();
    } else {
      setLoading(false);
    }
  }, [user]);

  const fetchMyCoupons = async () => {
    try {
      const response = await axios.get(`${API}/coupons/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCoupons(response.data);
    } catch (e) {
      console.error("Error fetching coupons:", e);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "active":
        return <Clock className="w-4 h-4" style={{ color: theme.colors.primary }} />;
      case "redeemed":
        return <CheckCircle className="w-4 h-4" style={{ color: theme.colors.textSecondary }} />;
      case "expired":
        return <XCircle className="w-4 h-4 text-red-500" />;
      default:
        return null;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case "active":
        return "Aktiv";
      case "redeemed":
        return "Eingelöst";
      case "expired":
        return "Abgelaufen";
      default:
        return status;
    }
  };

  if (!user) {
    return (
      <div 
        className="max-w-md mx-auto min-h-screen flex flex-col items-center justify-center px-4 transition-colors"
        style={{ backgroundColor: theme.colors.bgMain }}
      >
        <Ticket 
          className="w-16 h-16 mb-4" 
          style={{ color: theme.colors.textSecondary }}
        />
        <h2 
          className="text-2xl font-bold text-center mb-2"
          style={{ color: theme.colors.textPrimary }}
        >
          Melde dich an
        </h2>
        <p 
          className="text-center mb-6"
          style={{ color: theme.colors.textSecondary }}
        >
          Um deine Coupons zu sehen, musst du angemeldet sein
        </p>
        <Button
          onClick={() => setShowAuthModal(true)}
          style={{ backgroundColor: theme.colors.primary }}
          className="text-white font-bold px-8"
          data-testid="login-button"
        >
          Anmelden
        </Button>
      </div>
    );
  }

  return (
    <div 
      className="max-w-md mx-auto min-h-screen pb-safe transition-colors"
      style={{ backgroundColor: theme.colors.bgMain }}
    >
      {/* Header */}
      <header 
        className="sticky top-0 z-30 px-4 py-4"
        style={{ 
          backgroundColor: `${theme.colors.bgMain}ee`,
          backdropFilter: 'blur(10px)',
          borderBottom: `1px solid ${theme.colors.border}`
        }}
      >
        <div className="flex items-center justify-between">
          <div>
            <h1 
              className="text-2xl font-bold"
              style={{ color: theme.colors.textPrimary }}
            >
              Meine Coupons
            </h1>
            <p 
              className="text-xs"
              style={{ color: theme.colors.textSecondary }}
            >
              {coupons.filter(c => c.status === "active").length} aktive Coupons
            </p>
          </div>
          <Ticket className="w-6 h-6" style={{ color: theme.colors.primary }} />
        </div>
      </header>

      {/* Coupons List */}
      <div className="px-4 py-6" data-testid="my-coupons-list">
        {loading ? (
          <div className="space-y-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <div 
                key={i} 
                className="h-24 rounded-2xl animate-pulse"
                style={{ backgroundColor: theme.colors.bgCard }}
              />
            ))}
          </div>
        ) : coupons.length === 0 ? (
          <div className="text-center py-12">
            <Ticket 
              className="w-12 h-12 mx-auto mb-4" 
              style={{ color: theme.colors.textSecondary }}
            />
            <p style={{ color: theme.colors.textSecondary }}>
              Du hast noch keine Coupons eingelöst
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {coupons.map((coupon) => (
              <div
                key={coupon.id}
                className="rounded-2xl overflow-hidden cursor-pointer transition-all hover:scale-[1.01]"
                style={{ 
                  backgroundColor: theme.colors.bgCard,
                  border: `1px solid ${theme.colors.border}`,
                  opacity: coupon.status !== "active" ? 0.6 : 1
                }}
                onClick={() => coupon.status === "active" && setSelectedCoupon(coupon)}
                data-testid={`coupon-item-${coupon.code}`}
              >
                <div className="flex gap-4 p-4">
                  <img
                    src={coupon.image_url}
                    alt={coupon.title}
                    className="w-20 h-20 rounded-xl object-cover"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      {getStatusIcon(coupon.status)}
                      <span 
                        className="text-xs font-medium"
                        style={{ 
                          color: coupon.status === 'active' ? theme.colors.primary : theme.colors.textSecondary
                        }}
                      >
                        {getStatusText(coupon.status)}
                      </span>
                    </div>
                    <h3 
                      className="text-lg font-bold truncate"
                      style={{ color: theme.colors.textPrimary }}
                    >
                      {coupon.title}
                    </h3>
                    <p 
                      className="text-xs truncate"
                      style={{ color: theme.colors.textSecondary }}
                    >
                      Code: {coupon.code}
                    </p>
                    {coupon.status === "active" && (
                      <p 
                        className="text-xs mt-1"
                        style={{ color: theme.colors.primary }}
                      >
                        Läuft ab {formatDistanceToNow(new Date(coupon.expires_at), {
                          addSuffix: true,
                          locale: de
                        })}
                      </p>
                    )}
                    {coupon.status === "redeemed" && coupon.redeemed_at && (
                      <p 
                        className="text-xs mt-1"
                        style={{ color: theme.colors.textSecondary }}
                      >
                        Eingelöst {formatDistanceToNow(new Date(coupon.redeemed_at), {
                          addSuffix: true,
                          locale: de
                        })}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* QR Code Modal */}
      {selectedCoupon && (
        <QRCodeModal
          coupon={selectedCoupon}
          onClose={() => setSelectedCoupon(null)}
        />
      )}
    </div>
  );
}
